/**
 * SearchItemDetailsView — техническая вкладка «Детали» (M2-A).
 *
 * Показывает проверяемость результата из базы истины: источники (URL,
 * домен, trust), историю запусков (trigger / статус / запросы / страницы /
 * credits), причины статуса. Без debug / логов / LLM-промптов — их в
 * DTO SearchItemDetails нет по контракту бэкенда.
 */
import { useTranslation } from 'react-i18next';

import { reasonLabel, statusLabel } from '@/components/search/reasonLabel';

export interface DetailsSource {
  id: string;
  url: string | null;
  domain: string | null;
  title: string | null;
  trust: string | null;
  fetched_at: string | null;
}

export interface DetailsRun {
  id: string;
  trigger: string;
  status: string;
  tier_key: string | null;
  is_test_run: boolean;
  queries_count: number;
  pages_fetched: number;
  facts_count: number;
  credits_spent: number;
  started_at: string | null;
  finished_at: string | null;
}

export interface SearchItemDetails {
  sources: DetailsSource[];
  runs: DetailsRun[];
  reasons: string[];
}

export function SearchItemDetailsView({ details }: { details: SearchItemDetails }) {
  const { t } = useTranslation();

  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : '—';

  return (
    <div className="space-y-4">
      {/* Источники */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-surface-400 mb-2">
          {t('search.details.sources', { defaultValue: 'Sources' })}
          <span className="ml-1 font-mono normal-case">
            ({details.sources.length})
          </span>
        </h4>
        {details.sources.length === 0 ? (
          <p className="text-sm text-surface-500">
            {t('search.details.noSources', { defaultValue: 'No sources fetched.' })}
          </p>
        ) : (
          <ul className="space-y-1">
            {details.sources.map((s) => (
              <li key={s.id} className="text-xs text-surface-300 flex items-baseline gap-2">
                {s.url ? (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-primary-300 hover:underline truncate max-w-[24rem]"
                  >
                    {s.domain || s.url}
                  </a>
                ) : (
                  <span>{s.domain || '—'}</span>
                )}
                {s.title && (
                  <span className="text-surface-500 truncate max-w-[20rem]" title={s.title}>
                    {s.title}
                  </span>
                )}
                {s.trust && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-800 text-surface-400">
                    {t(`search.details.trust.${s.trust}`, { defaultValue: s.trust })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Прогоны */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-surface-400 mb-2">
          {t('search.details.runs', { defaultValue: 'Search runs' })}
          <span className="ml-1 font-mono normal-case">({details.runs.length})</span>
        </h4>
        {details.runs.length === 0 ? (
          <p className="text-sm text-surface-500">
            {t('search.details.noRuns', { defaultValue: 'No runs recorded.' })}
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-surface-500 text-left">
                <th className="py-1 pr-2 font-normal">
                  {t('search.details.runTrigger', { defaultValue: 'Trigger' })}
                </th>
                <th className="py-1 pr-2 font-normal">
                  {t('search.details.runStatus', { defaultValue: 'Status' })}
                </th>
                <th className="py-1 pr-2 font-normal">
                  {t('search.details.runQueries', { defaultValue: 'Queries' })}
                </th>
                <th className="py-1 pr-2 font-normal">
                  {t('search.details.runPages', { defaultValue: 'Pages' })}
                </th>
                <th className="py-1 pr-2 font-normal">
                  {t('search.details.runFacts', { defaultValue: 'Facts' })}
                </th>
                <th className="py-1 pr-2 font-normal">
                  {t('search.details.runCredits', { defaultValue: 'Credits' })}
                </th>
                <th className="py-1 font-normal">
                  {t('search.details.runStarted', { defaultValue: 'Started' })}
                </th>
              </tr>
            </thead>
            <tbody className="text-surface-300">
              {details.runs.map((r) => (
                <tr key={r.id} className="border-t border-surface-800/60">
                  <td className="py-1 pr-2">
                    {t(`search.details.trigger.${r.trigger}`, { defaultValue: r.trigger })}
                    {r.is_test_run && (
                      <span className="ml-1 text-[10px] text-surface-500">
                        ({t('search.details.testRun', { defaultValue: 'test' })})
                      </span>
                    )}
                  </td>
                  <td className="py-1 pr-2">
                    {statusLabel(t, r.status)}
                  </td>
                  <td className="py-1 pr-2 font-mono tabular-nums">{r.queries_count}</td>
                  <td className="py-1 pr-2 font-mono tabular-nums">{r.pages_fetched}</td>
                  <td className="py-1 pr-2 font-mono tabular-nums">{r.facts_count}</td>
                  <td className="py-1 pr-2 font-mono tabular-nums">{r.credits_spent}</td>
                  <td className="py-1">{fmt(r.started_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Причины */}
      {details.reasons.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-surface-400 mb-2">
            {t('search.details.reasons', { defaultValue: 'Status reasons' })}
          </h4>
          <ul className="list-disc list-inside text-xs text-surface-300">
            {details.reasons.map((r) => (
              <li key={r}>{reasonLabel(t, r)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
